import { Message } from '@gonfalon/messages/Message';
import { Code } from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';

import type { EmptyStateContent } from '../types/command';

import styles from './command-palette-autocomplete.module.css';

/**
 * Empty-state variants rendered when the listbox has no rows. Each
 * is a pure leaf component: receives its inputs as props, returns
 * JSX, no hooks or side effects. The orchestrator (CommandPaletteResults)
 * decides which to mount.
 */

/**
 * Generic fallback. Shown when the user has typed a query that
 * matches nothing across the visible candidate set.
 */
export function NoMatchesEmptyState() {
  return (
    <div className={styles.empty}>
      <Message id="commandPalette.results.noMatches" />
    </div>
  );
}

/**
 * Snap-prefix discovery hint. Shown in main mode when the user
 * typed `<prefix><something>` but `<something>` doesn't match any
 * registered snap. Suggests one example registered snap so the user
 * learns the vocabulary.
 */
export function SnapDiscoveryEmptyState({ prefix, firstKey }: { prefix: string; firstKey: string }) {
  return (
    <div className={styles.empty}>
      <Message
        id="commandPalette.results.tryExample"
        values={{ example: `${prefix}${firstKey}`, code: (chunks) => <Code size="small">{chunks}</Code> }}
      />
    </div>
  );
}

/**
 * Source-declared empty state. Shown inside an entered root when
 * the root's `emptyState` config resolved to content for the
 * current query (e.g. "Search flags by name, key, or description...").
 * Overrides the generic copy with phrasing closer to the source's
 * vocabulary.
 */
export function RootEmptyState({ content }: { content: EmptyStateContent }) {
  return (
    <div className={styles.emptyState}>
      {content.icon !== undefined && <Icon name={content.icon} size="medium" className={styles.emptyStateIcon} />}
      <div className={styles.emptyStateLabel}>{content.label}</div>
      {content.description !== undefined && <div className={styles.emptyStateDescription}>{content.description}</div>}
    </div>
  );
}

/**
 * Source-declared loading state. Shown inside an entered root when
 * the root's `status === 'pending'` AND it declared `loadingState`.
 * Takes precedence over `emptyState` so the user doesn't briefly see
 * the no-match copy while a fetch is in flight.
 *
 * Same visual shape as `RootEmptyState` (icon + label + optional
 * description) -- the difference is purely semantic (the consumer's
 * copy describes the loading action, not the no-result outcome).
 */
export function LoadingEmptyState({ content }: { content: EmptyStateContent }) {
  return (
    <div className={styles.emptyState}>
      {content.icon !== undefined && <Icon name={content.icon} size="medium" className={styles.emptyStateIcon} />}
      <div className={styles.emptyStateLabel}>{content.label}</div>
      {content.description !== undefined && <div className={styles.emptyStateDescription}>{content.description}</div>}
    </div>
  );
}
